import { useEffect, useRef, useState } from 'react'

export interface Size {
    width: number
    height: number
}

/**
 * Mede um elemento e reage a resize. O palco usa isso pra recalcular o
 * mosaico sempre que a janela, a sidebar ou o filmstrip mudam de tamanho.
 */
export function useElementSize<T extends HTMLElement>() {
    const ref = useRef<T | null>(null)
    const [size, setSize] = useState<Size>({ width: 0, height: 0 })

    useEffect(() => {
        const element = ref.current
        if (!element) return

        const measure = () => setSize((atual) => {
            const width = element.clientWidth
            const height = element.clientHeight
            /**
             * Sai calado quando nada mudou.
             *
             * O ResizeObserver dispara em rajada — uma vez por quadro enquanto
             * a sidebar anima, por exemplo — e cada `setState` daqui
             * re-renderiza o palco inteiro, com um `<video>` por tile. Como o
             * objeto era novo a cada medida, o React nao tinha como ver que a
             * resposta era a mesma de antes.
             */
            return atual.width === width && atual.height === height
                ? atual
                : { width, height }
        })
        measure()

        // jsdom (testes) nao tem ResizeObserver — cai no resize da janela.
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure)
            return () => window.removeEventListener('resize', measure)
        }

        const observer = new ResizeObserver(measure)
        observer.observe(element)
        return () => observer.disconnect()
    }, [])

    return { ref, size }
}
